/**
 * 학부모 소통 페이지
 * Parent Communication Page
 *
 * 전체 공지 및 개별 메시지 관리 기능 제공
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { API_BASE_URL } from '../../../config/api';

// ==================== Types ====================

interface Post {
  id: string;
  post_type: 'announcement' | 'individual';
  target_student_id?: string;
  target_student_name?: string;
  recipient_kind?: 'parent' | 'student';
  unread?: boolean;
  title: string;
  content: string;
  is_pinned: boolean;
  read_count: number;
  comment_count: number;
  created_at: string;
  updated_at: string;
  // 마지막으로 오간 메시지. 답장은 댓글로 쌓여 created_at 을 바꾸지 않으므로
  // 목록 정렬·미리보기는 반드시 이 값을 봐야 한다.
  last_message_at?: string | null;
  last_message_content?: string | null;
}

interface PostDetail extends Post {
  comments: Comment[];
}

interface Comment {
  id: string;
  post_id: string;
  author_name: string;
  author_type: 'owner' | 'staff' | 'parent';
  content: string;
  created_at: string;
}

interface Student {
  id: string;
  name: string;
  phone?: string;
}

interface PostsResponse {
  success: boolean;
  data: {
    posts: Post[];
    total_count: number;
    page: number;
    limit: number;
    total_pages: number;
    announcement_count: number;
    message_count: number;
  };
}

// ==================== API Helper ====================

const getApiUrl = (path: string) => {
  const baseUrl = API_BASE_URL === '' ? '/api' : API_BASE_URL;
  return `${baseUrl}${path}`;
};

const getAuthToken = () => {
  return localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
};

// ==================== Component ====================

const ParentCommunicationPage: React.FC = () => {
  // 상태
  const [posts, setPosts] = useState<Post[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [announcementCount, setAnnouncementCount] = useState(0);
  const [messageCount, setMessageCount] = useState(0);
  const [unreadTotal, setUnreadTotal] = useState(0);

  // 필터 상태 (백엔드는 'individual'을 사용)
  // 🔴 기본은 개별 메시지다. 실사용이 개별 123건 : 공지 6건이고, 개별의 89%에
  //    학부모 답장이 달린다(총 845건). 공지를 먼저 띄우면 거의 안 쓰는 화면을 먼저 본다.
  const [postType, setPostType] = useState<'announcement' | 'individual'>('individual');

  // UI 상태
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // 모달 상태
  const [showWriteModal, setShowWriteModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState<PostDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // 글쓰기 폼 상태
  const [writeForm, setWriteForm] = useState({
    post_type: 'announcement' as 'announcement' | 'individual',
    target_student_id: '',
    title: '',
    content: '',
    is_pinned: false,
  });
  const [writeLoading, setWriteLoading] = useState(false);

  // 학생 검색 상태
  const [students, setStudents] = useState<Student[]>([]);
  const [studentSearch, setStudentSearch] = useState('');
  const [studentLoading, setStudentLoading] = useState(false);

  // 댓글 상태
  const [commentContent, setCommentContent] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // 개별 메시지 = 채팅 모드 상태
  const [chatStudent, setChatStudent] = useState<{ id: string; name: string } | null>(null);
  const [chatKind, setChatKind] = useState<'parent' | 'student'>('parent');
  const [chatThreadId, setChatThreadId] = useState<string | null>(null);
  const [chatPost, setChatPost] = useState<{ content: string; created_at: string; author_name?: string } | null>(null);
  const [chatComments, setChatComments] = useState<Comment[]>([]);
  const [chatRecipientReadAt, setChatRecipientReadAt] = useState<string | null>(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [chatStudentSearch, setChatStudentSearch] = useState('');
  const chatScrollRef = useRef<HTMLDivElement>(null);

  // API 호출: 글 목록 조회
  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const authToken = getAuthToken();
      const params = new URLSearchParams();
      params.append('page', String(currentPage));
      // 개별 메시지 탭은 대화 목록이라 페이지 넘김이 없다. 20개로 끊으면
      // 그 밖의 대화는 이름으로 검색하지 않는 한 아예 보이지 않는다.
      params.append('limit', postType === 'individual' ? '100' : '20');
      params.append('post_type', postType);

      const response = await fetch(getApiUrl(`/v1/owner/posts?${params.toString()}`), {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result: PostsResponse = await response.json();
        if (result.success) {
          setPosts(result.data.posts);
          setTotalCount(result.data.total_count);
          setTotalPages(result.data.total_pages);
          setAnnouncementCount(result.data.announcement_count);
          setMessageCount(result.data.message_count);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || '글 목록을 불러오는데 실패했습니다.');
      }
    } catch (err) {
      console.error('글 목록 조회 실패:', err);
      setError('글 목록을 가져오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, postType]);

  // API 호출: 글 상세 조회
  const fetchPostDetail = async (postId: string) => {
    try {
      setDetailLoading(true);
      const authToken = getAuthToken();

      const response = await fetch(getApiUrl(`/v1/owner/posts/${postId}`), {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // API가 { data: { post, comments, reads } } 형식으로 반환
          const postData = result.data.post || result.data;
          setSelectedPost({
            ...postData,
            comments: result.data.comments || [],
            reads: result.data.reads || []
          });
          setShowDetailModal(true);
        }
      } else {
        const errorData = await response.json();
        alert(errorData.detail || '글 상세 정보를 불러오는데 실패했습니다.');
      }
    } catch (err) {
      console.error('글 상세 조회 실패:', err);
      alert('글 상세 정보를 가져오는 중 오류가 발생했습니다.');
    } finally {
      setDetailLoading(false);
    }
  };

  // API 호출: 글 작성
  const handleSubmitPost = async () => {
    if (!writeForm.title.trim()) {
      alert('제목을 입력해주세요.');
      return;
    }
    if (!writeForm.content.trim()) {
      alert('내용을 입력해주세요.');
      return;
    }
    if (writeForm.post_type === 'individual' && !writeForm.target_student_id) {
      alert('학생을 선택해주세요.');
      return;
    }

    try {
      setWriteLoading(true);
      const authToken = getAuthToken();

      const response = await fetch(getApiUrl('/v1/owner/posts'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          post_type: writeForm.post_type,
          target_student_id: writeForm.post_type === 'individual' ? writeForm.target_student_id : null,
          title: writeForm.title,
          content: writeForm.content,
          is_pinned: writeForm.is_pinned,
        })
      });

      if (response.ok) {
        alert('글이 작성되었습니다.');
        setShowWriteModal(false);
        resetWriteForm();
        fetchPosts();
      } else {
        const errorData = await response.json();
        alert(errorData.detail || '글 작성에 실패했습니다.');
      }
    } catch (err) {
      console.error('글 작성 실패:', err);
      alert('글 작성 중 오류가 발생했습니다.');
    } finally {
      setWriteLoading(false);
    }
  };

  // API 호출: 댓글 작성
  const handleSubmitComment = async () => {
    if (!commentContent.trim()) {
      alert('댓글 내용을 입력해주세요.');
      return;
    }
    if (!selectedPost) return;

    try {
      setCommentLoading(true);
      const authToken = getAuthToken();

      const response = await fetch(getApiUrl(`/v1/owner/posts/${selectedPost.id}/comments`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content: commentContent
        })
      });

      if (response.ok) {
        setCommentContent('');
        // 댓글 목록 새로고침
        fetchPostDetail(selectedPost.id);
        fetchPosts(); // 댓글 수 업데이트
      } else {
        const errorData = await response.json();
        alert(errorData.detail || '댓글 작성에 실패했습니다.');
      }
    } catch (err) {
      console.error('댓글 작성 실패:', err);
      alert('댓글 작성 중 오류가 발생했습니다.');
    } finally {
      setCommentLoading(false);
    }
  };

  // API 호출: 글 삭제
  const handleDeletePost = async (postId: string) => {
    if (!window.confirm('이 글을 삭제하시겠습니까?\n댓글과 읽음 기록도 함께 삭제됩니다.')) {
      return;
    }

    try {
      setDeleteLoading(true);
      const authToken = getAuthToken();

      const response = await fetch(getApiUrl(`/v1/owner/posts/${postId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        alert('글이 삭제되었습니다.');
        setShowDetailModal(false);
        setSelectedPost(null);
        fetchPosts();
      } else {
        const errorData = await response.json();
        alert(errorData.detail || '글 삭제에 실패했습니다.');
      }
    } catch (err) {
      console.error('글 삭제 실패:', err);
      alert('글 삭제 중 오류가 발생했습니다.');
    } finally {
      setDeleteLoading(false);
    }
  };

  // 채팅: 학생+대상 단위 통합 스레드 로드
  // ⭐ 2026-07-23: 글(postId) 단위 → 학생+kind 단위 통합. 대화가 여러 글로 갈라져
  //   있어도(새 개별글 발송 등) 전부 시간순 한 줄기로 보인다 — "학부모가 보낸
  //   메시지가 채팅창에 안 보이던" 파편화 문제의 근본 해결.
  const loadChatThread = async (studentId: string, kind: 'parent' | 'student') => {
    try {
      setChatLoading(true);
      const authToken = getAuthToken();
      const response = await fetch(
        getApiUrl(`/v1/owner/posts/thread?student_id=${encodeURIComponent(studentId)}&kind=${kind}`),
        { headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' } }
      );
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // 글 본문도 messages에 포함돼 오므로 chatPost는 쓰지 않는다
          setChatThreadId(result.data.latest_post_id ? String(result.data.latest_post_id) : null);
          setChatPost(null);
          setChatComments(result.data.messages || []);
          setChatRecipientReadAt(result.data.recipient_read_at || null);
          // 열람 시 백엔드가 스레드 전체 운영자 읽음 처리 → 목록·카운트 갱신
          fetchPosts();
          fetchUnreadTotal();
        }
      }
    } catch (err) {
      console.error('대화 로드 실패:', err);
    } finally {
      setChatLoading(false);
    }
  };

  // 채팅: 학생+대상(학부모/학생) 선택 → 통합 스레드 로드 (글이 없으면 빈 대화)
  const openChat = (student: { id: string; name: string }, kind: 'parent' | 'student' = 'parent') => {
    setChatStudent(student);
    setChatKind(kind);
    setChatInput('');
    setChatThreadId(null);
    setChatPost(null);
    setChatComments([]);
    loadChatThread(student.id, kind);
  };

  // 채팅: 메시지 전송 (스레드 없으면 자동 생성)
  const sendChatMessage = async () => {
    const text = chatInput.trim();
    if (!text || !chatStudent || chatSending) return;
    try {
      setChatSending(true);
      const authToken = getAuthToken();
      if (chatThreadId) {
        const response = await fetch(getApiUrl(`/v1/owner/posts/${chatThreadId}/comments`), {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: text })
        });
        if (response.ok) {
          setChatInput('');
          await loadChatThread(chatStudent.id, chatKind);
        } else {
          const e = await response.json();
          alert(e.detail || '전송에 실패했습니다.');
        }
      } else {
        const response = await fetch(getApiUrl('/v1/owner/posts'), {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            post_type: 'individual',
            target_student_id: chatStudent.id,
            recipient_kind: chatKind,
            title: `${chatStudent.name} 대화`,
            content: text,
            is_pinned: false,
          })
        });
        if (response.ok) {
          setChatInput('');
          await loadChatThread(chatStudent.id, chatKind);
          fetchPosts();
        } else {
          const e = await response.json();
          alert(e.detail || '전송에 실패했습니다.');
        }
      }
      fetchPosts();
    } catch (err) {
      console.error('채팅 전송 실패:', err);
      alert('전송 중 오류가 발생했습니다.');
    } finally {
      setChatSending(false);
    }
  };

  // 채팅: 대화 목록 (학생별 단일 = 최신 글 기준)
  const conversations = useMemo(() => {
    const map = new Map<
      string,
      { studentId: string; studentName: string; kind: 'parent' | 'student'; lastText: string; lastAt: string; unread: boolean }
    >();
    for (const p of posts) {
      if (p.post_type !== 'individual' || !p.target_student_id) continue;
      const kind = (p.recipient_kind || 'parent') as 'parent' | 'student';
      const key = `${p.target_student_id}::${kind}`;
      const prev = map.get(key);
      // 답장(댓글)이 있으면 그 시각·내용이 이 대화의 마지막 소식이다
      const activityAt = p.last_message_at || p.created_at;
      const activityText = p.last_message_content || p.content || p.title || '';
      const newer = !prev || new Date(activityAt).getTime() > new Date(prev.lastAt).getTime();
      map.set(key, {
        studentId: p.target_student_id,
        studentName: p.target_student_name || '학생',
        kind,
        lastText: newer ? activityText : (prev?.lastText || ''),
        lastAt: newer ? activityAt : (prev?.lastAt || activityAt),
        unread: (prev?.unread || false) || !!p.unread,
      });
    }
    // 🔴 안 읽은 대화를 맨 위로. 종전엔 시간순이라 어제 온 답장이 아래로 밀려
    //    원장이 못 보고 지나쳤다(학부모는 무시당했다고 느낀다).
    return Array.from(map.values()).sort((a, b) => {
      if (a.unread !== b.unread) return a.unread ? -1 : 1;
      return new Date(b.lastAt).getTime() - new Date(a.lastAt).getTime();
    });
  }, [posts]);

  // 들어오자마자 첫 대화를 연다 — 카톡처럼. 종전엔 오른쪽이 텅 빈 채로
  // "왼쪽에서 학생을 선택하세요" 만 떠서 한 번 더 눌러야 했다.
  // 정렬이 '안 읽은 것 먼저'라 첫 번째가 곧 가장 급한 대화다.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (postType !== 'individual') return;
    if (autoOpenedRef.current || chatStudent) return;
    if (conversations.length === 0) return;
    autoOpenedRef.current = true;
    const first = conversations[0];
    openChat({ id: first.studentId, name: first.studentName }, first.kind);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postType, conversations, chatStudent]);


  // 채팅: 새 메시지 시 맨 아래로 스크롤
  useEffect(() => {
    const el = chatScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chatComments, chatPost]);

  // 개별 탭: 화면 열려있고 보일 때 25초마다 목록 자동 새로고침 (안읽음 갱신)
  useEffect(() => {
    if (postType !== 'individual') return;
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') fetchPosts();
    }, 25000);
    return () => clearInterval(t);
  }, [postType, fetchPosts]);

  // 탭과 무관하게 안읽음 총개수 (경량 API) — 개별 탭 클릭 전에도 표시
  const fetchUnreadTotal = useCallback(async () => {
    try {
      const authToken = getAuthToken();
      const res = await fetch(getApiUrl('/v1/owner/posts/unread-count'), {
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        setUnreadTotal(Number(data?.data?.unread_count ?? 0));
      }
    } catch {
      /* 무시 */
    }
  }, []);

  useEffect(() => {
    fetchUnreadTotal();
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') fetchUnreadTotal();
    }, 30000);
    return () => clearInterval(t);
  }, [fetchUnreadTotal]);

  // API 호출: 학생 검색
  const searchStudents = async (query: string) => {
    if (!query.trim()) {
      setStudents([]);
      return;
    }

    try {
      setStudentLoading(true);
      const authToken = getAuthToken();

      const response = await fetch(getApiUrl(`/v1/owner/members?search=${encodeURIComponent(query)}&limit=10`), {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        // API가 { members, total, page, limit } 형식으로 직접 반환
        setStudents(result.members || []);
      }
    } catch (err) {
      console.error('학생 검색 실패:', err);
    } finally {
      setStudentLoading(false);
    }
  };

  // 폼 초기화
  const resetWriteForm = () => {
    setWriteForm({
      post_type: 'announcement',
      target_student_id: '',
      title: '',
      content: '',
      is_pinned: false,
    });
    setStudentSearch('');
    setStudents([]);
  };

  // 데이터 로드
  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  // 학생 검색 디바운스
  useEffect(() => {
    const timer = setTimeout(() => {
      if (writeForm.post_type === 'individual') {
        searchStudents(studentSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [studentSearch, writeForm.post_type]);

  // 채팅 학생 검색 디바운스 (개별 탭)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (postType === 'individual' && chatStudentSearch.trim()) {
        searchStudents(chatStudentSearch);
      } else if (postType === 'individual') {
        setStudents([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [chatStudentSearch, postType]);

  // 탭 변경
  const handleTabChange = (type: 'announcement' | 'individual') => {
    setPostType(type);
    setCurrentPage(1);
    if (type === 'individual') {
      setChatStudent(null);
      setChatThreadId(null);
      setChatPost(null);
      setChatComments([]);
      setChatStudentSearch('');
      setStudents([]);
    }
  };

  // 날짜 포맷
  // 대화 목록의 시각 — 카톡처럼 읽는다. '08. 24.' 보다 '오후 3:20' 이 훨씬 빠르게 들어온다.
  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const sameDay = (a: Date, b: Date) =>
      a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    if (sameDay(d, now)) {
      return d.toLocaleTimeString('ko-KR', { hour: 'numeric', minute: '2-digit' });
    }
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (sameDay(d, yesterday)) return '어제';
    if (d.getFullYear() === now.getFullYear()) {
      return d.toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric' });
    }
    return d.toLocaleDateString('ko-KR', { year: '2-digit', month: 'numeric', day: 'numeric' });
  };

  // 시간 포맷 (상세용)
  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* 헤더 — 탭을 없애고 한 줄에 모았다 (2026-09-10 대표 지시)
          이 화면은 사실상 '개별 메시지'다(실사용 123건 : 공지 6건). 가로를 반씩
          차지하던 탭 대신, 공지는 제목 옆 작은 버튼으로 들어간다. */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <h1 className="text-2xl font-bold text-gray-900">
          {postType === 'individual' ? '학부모 소통' : '전체 공지'}
        </h1>
        {unreadTotal > 0 && postType === 'individual' && (
          <span className="inline-flex items-center text-xs font-bold text-white bg-red-500 rounded-full px-2 py-0.5">
            새 답장 {unreadTotal}
          </span>
        )}

        <div className="ml-auto flex items-center gap-2">
          {postType === 'individual' ? (
            <button
              onClick={() => handleTabChange('announcement')}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              title="전 학부모에게 한 번에 보내는 공지"
            >
              📢 전체 공지
              <span className="text-gray-400">{announcementCount}</span>
            </button>
          ) : (
            <button
              onClick={() => handleTabChange('individual')}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              ← 대화로 돌아가기
              {unreadTotal > 0 && (
                <span className="ml-1 inline-flex items-center text-[10px] font-bold text-white bg-red-500 rounded-full px-1.5">
                  {unreadTotal}
                </span>
              )}
            </button>
          )}
          <button
            onClick={() => {
              resetWriteForm();
              setShowWriteModal(true);
            }}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1 text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {postType === 'individual' ? '새 대화' : '공지 쓰기'}
          </button>
        </div>
      </div>

      {/* 에러 메시지 */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* 개별 메시지 = 채팅 / 공지 = 테이블 */}
      {postType === 'individual' ? (
        <div className="bg-white rounded-lg shadow flex h-[75vh] overflow-hidden">
          {/* 좌: 학생 검색 + 대화 목록
              🔴 좁은 화면(태블릿 세로·휴대폰)에서는 좌우 2단이 둘 다 답답하다.
                 카톡 휴대폰처럼 목록만 보이다가, 대화를 고르면 대화가 전체를 쓴다. */}
          <div className={`${chatStudent ? 'hidden md:flex' : 'flex'} w-full md:w-72 border-r border-gray-200 flex-col`}>
            <div className="p-3 border-b border-gray-200">
              <input
                type="text"
                value={chatStudentSearch}
                onChange={(e) => setChatStudentSearch(e.target.value)}
                placeholder="학생 이름 검색"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex-1 overflow-y-auto">
              {chatStudentSearch.trim() ? (
                <div>
                  {studentLoading ? (
                    <p className="p-4 text-sm text-gray-400">검색 중...</p>
                  ) : students.length === 0 ? (
                    <p className="p-4 text-sm text-gray-400">검색 결과 없음</p>
                  ) : (
                    students.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => {
                          openChat({ id: s.id, name: s.name });
                          setChatStudentSearch('');
                          setStudents([]);
                        }}
                        className="w-full text-left px-4 py-3 text-sm hover:bg-blue-50 border-b border-gray-100"
                      >
                        <span className="font-medium text-gray-900">{s.name}</span>
                        {s.phone && <span className="text-gray-400 ml-2">{s.phone}</span>}
                      </button>
                    ))
                  )}
                </div>
              ) : conversations.length === 0 ? (
                <p className="p-4 text-sm text-gray-400">대화가 없습니다. 위에서 학생을 검색해 시작하세요.</p>
              ) : (
                conversations.map((c) => (
                  <button
                    key={`${c.studentId}::${c.kind}`}
                    onClick={() => openChat({ id: c.studentId, name: c.studentName }, c.kind)}
                    className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 ${
                      chatStudent?.id === c.studentId && chatKind === c.kind ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex justify-between items-baseline">
                      <span className={`truncate ${c.unread ? 'font-bold text-gray-900' : 'font-medium text-gray-900'}`}>
                        {c.unread && (
                          <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-1.5 align-middle" />
                        )}
                        {c.studentName}
                        <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full ${
                          c.kind === 'student' ? 'bg-amber-100 text-amber-700' : 'bg-indigo-100 text-indigo-700'
                        }`}>
                          {c.kind === 'student' ? '학생' : '학부모'}
                        </span>
                      </span>
                      <span className="text-[10px] text-gray-400 ml-2 shrink-0">{formatDate(c.lastAt)}</span>
                    </div>
                    <p className={`text-xs truncate mt-0.5 ${c.unread ? 'text-gray-700 font-medium' : 'text-gray-500'}`}>{c.lastText}</p>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* 우: 채팅창 */}
          <div className={`${chatStudent ? 'flex' : 'hidden md:flex'} flex-1 flex-col`}>
            {!chatStudent ? (
              <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
                왼쪽에서 학생을 골라 대화를 시작하세요
              </div>
            ) : (
              <>
                <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1 min-w-0">
                    {/* 좁은 화면에서는 목록이 숨으므로 돌아갈 길을 준다 (카톡과 같은 자리) */}
                    <button
                      onClick={() => setChatStudent(null)}
                      className="md:hidden -ml-1 p-1 text-gray-500 hover:text-gray-800 shrink-0"
                      title="대화 목록으로"
                      aria-label="대화 목록으로"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <span className="font-semibold text-gray-900 truncate">{chatStudent.name}</span>
                  </div>
                  <div className="flex rounded-lg overflow-hidden border border-gray-300 text-sm">
                    <button
                      onClick={() => openChat(chatStudent, 'parent')}
                      className={`px-3 py-1 ${chatKind === 'parent' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >
                      학부모
                    </button>
                    <button
                      onClick={() => openChat(chatStudent, 'student')}
                      className={`px-3 py-1 border-l border-gray-300 ${chatKind === 'student' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >
                      학생
                    </button>
                  </div>
                </div>
                <div ref={chatScrollRef} className="flex-1 overflow-y-auto p-4 bg-gray-50 space-y-3">
                  {chatLoading ? (
                    <p className="text-center text-gray-400 text-sm py-6">불러오는 중...</p>
                  ) : (
                    <>
                      {chatPost && (
                        <div className="flex justify-end">
                          <div className="max-w-[75%] flex flex-col items-end">
                            <div className="flex items-end gap-1.5">
                              <span className="text-[10px] text-gray-400 whitespace-nowrap">
                                {formatDateTime(chatPost.created_at)}
                              </span>
                              <div className="px-3 py-2 rounded-2xl rounded-br-sm bg-blue-600 text-white whitespace-pre-wrap break-words">
                                {chatPost.content}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {chatComments.map((comment) => {
                        const mine = comment.author_type === 'owner' || comment.author_type === 'staff';
                        return (
                          <div key={comment.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[75%] flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
                              {!mine && (
                                <span className="text-xs text-gray-500 mb-0.5 ml-1">{comment.author_name}</span>
                              )}
                              <div className="flex items-end gap-1.5">
                                {mine && (
                                  <span className="text-[10px] text-gray-400 whitespace-nowrap">
                                    {formatDateTime(comment.created_at)}
                                  </span>
                                )}
                                <div
                                  className={`px-3 py-2 rounded-2xl whitespace-pre-wrap break-words ${
                                    mine
                                      ? 'bg-blue-600 text-white rounded-br-sm'
                                      : 'bg-white text-gray-800 border border-gray-200 rounded-bl-sm'
                                  }`}
                                >
                                  {comment.content}
                                </div>
                                {!mine && (
                                  <span className="text-[10px] text-gray-400 whitespace-nowrap">
                                    {formatDateTime(comment.created_at)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {!chatPost && chatComments.length === 0 && (
                        <p className="text-center text-gray-400 text-sm py-6">
                          첫 메시지를 보내 대화를 시작하세요.
                        </p>
                      )}
                      {(() => {
                        const ownerTimes: number[] = [];
                        if (chatPost) ownerTimes.push(new Date(chatPost.created_at).getTime());
                        chatComments.forEach((c) => {
                          if (c.author_type === 'owner' || c.author_type === 'staff') {
                            ownerTimes.push(new Date(c.created_at).getTime());
                          }
                        });
                        if (ownerTimes.length === 0) return null;
                        const lastOwnerAt = Math.max(...ownerTimes);
                        const read =
                          !!chatRecipientReadAt &&
                          new Date(chatRecipientReadAt).getTime() >= lastOwnerAt;
                        return (
                          <div className="flex justify-end">
                            <span className={`text-[11px] ${read ? 'text-blue-500' : 'text-gray-400'}`}>
                              {read ? '읽음' : '안읽음'}
                            </span>
                          </div>
                        );
                      })()}
                    </>
                  )}
                </div>
                <div className="flex gap-2 items-end p-3 border-t border-gray-200">
                  <textarea
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendChatMessage();
                      }
                    }}
                    placeholder="메시지를 입력하세요 (Enter 전송, Shift+Enter 줄바꿈)"
                    rows={1}
                    className="flex-1 border border-gray-300 rounded-2xl px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none max-h-32"
                  />
                  <button
                    onClick={sendChatMessage}
                    disabled={chatSending || !chatInput.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 transition-colors shrink-0"
                  >
                    {chatSending ? '...' : '전송'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">로딩 중...</div>
        ) : posts.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {postType === 'announcement' ? '등록된 공지가 없습니다.' : '등록된 메시지가 없습니다.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/2">
                    제목
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    작성일
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    읽음
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    댓글
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {posts.map((post) => (
                  <tr
                    key={post.id}
                    onClick={() => fetchPostDetail(post.id)}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center gap-2">
                        {post.is_pinned && (
                          <span className="text-red-500" title="상단 고정">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                            </svg>
                          </span>
                        )}
                        <span className="font-medium text-gray-900 truncate">
                          {post.title}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(post.created_at)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-center">
                      {post.read_count}명
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-center">
                      {post.comment_count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              총 {totalCount}건 중 {(currentPage - 1) * 20 + 1} - {Math.min(currentPage * 20, totalCount)}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 hover:bg-gray-50"
              >
                이전
              </button>
              <span className="px-3 py-1 text-sm text-gray-600">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 hover:bg-gray-50"
              >
                다음
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      {/* 글쓰기 모달 */}
      {showWriteModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowWriteModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <h2 className="text-lg font-bold text-gray-900">글쓰기</h2>
              <button
                onClick={() => setShowWriteModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              {/* 글 유형 선택 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">글 유형</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="post_type"
                      value="announcement"
                      checked={writeForm.post_type === 'announcement'}
                      onChange={(e) => setWriteForm({ ...writeForm, post_type: e.target.value as 'announcement' | 'individual', target_student_id: '' })}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">전체 공지</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="post_type"
                      value="individual"
                      checked={writeForm.post_type === 'individual'}
                      onChange={(e) => setWriteForm({ ...writeForm, post_type: e.target.value as 'announcement' | 'individual' })}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">개별 메시지</span>
                  </label>
                </div>
              </div>

              {/* 학생 선택 (개별 메시지인 경우) */}
              {writeForm.post_type === 'individual' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">학생 선택</label>
                  <input
                    type="text"
                    value={studentSearch}
                    onChange={(e) => setStudentSearch(e.target.value)}
                    placeholder="학생 이름을 검색하세요"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {studentLoading && (
                    <p className="text-sm text-gray-500 mt-1">검색 중...</p>
                  )}
                  {students.length > 0 && (
                    <ul className="mt-2 border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
                      {students.map((student) => (
                        <li
                          key={student.id}
                          onClick={() => {
                            setWriteForm({ ...writeForm, target_student_id: student.id });
                            setStudentSearch(student.name);
                            setStudents([]);
                          }}
                          className={`px-3 py-2 cursor-pointer hover:bg-gray-100 ${
                            writeForm.target_student_id === student.id ? 'bg-blue-50' : ''
                          }`}
                        >
                          <span className="font-medium">{student.name}</span>
                          {student.phone && (
                            <span className="text-gray-500 text-sm ml-2">{student.phone}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  {writeForm.target_student_id && (
                    <p className="text-sm text-blue-600 mt-1">
                      선택됨: {studentSearch}
                    </p>
                  )}
                </div>
              )}

              {/* 제목 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">제목</label>
                <input
                  type="text"
                  value={writeForm.title}
                  onChange={(e) => setWriteForm({ ...writeForm, title: e.target.value })}
                  placeholder="제목을 입력하세요"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* 내용 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">내용</label>
                <textarea
                  value={writeForm.content}
                  onChange={(e) => setWriteForm({ ...writeForm, content: e.target.value })}
                  placeholder="내용을 입력하세요"
                  rows={6}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                />
              </div>

              {/* 상단 고정 (전체 공지인 경우에만) */}
              {writeForm.post_type === 'announcement' && (
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={writeForm.is_pinned}
                      onChange={(e) => setWriteForm({ ...writeForm, is_pinned: e.target.checked })}
                      className="rounded text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">상단 고정</span>
                  </label>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowWriteModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleSubmitPost}
                disabled={writeLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {writeLoading ? '등록 중...' : '등록'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 글 상세 모달 */}
      {showDetailModal && selectedPost && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => {
            setShowDetailModal(false);
            setSelectedPost(null);
          }}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <div className="flex items-center gap-2">
                {selectedPost.is_pinned && (
                  <span className="text-red-500" title="상단 고정">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                    </svg>
                  </span>
                )}
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  selectedPost.post_type === 'announcement'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-green-100 text-green-700'
                }`}>
                  {selectedPost.post_type === 'announcement' ? '공지' : '메시지'}
                </span>
              </div>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedPost(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-4">
              {/* 글 정보 */}
              <h2 className="text-xl font-bold text-gray-900 mb-2">{selectedPost.title}</h2>
              <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                <span>{formatDateTime(selectedPost.created_at)}</span>
                <span>읽음 {selectedPost.read_count}명</span>
                {selectedPost.target_student_name && (
                  <span>대상: {selectedPost.target_student_name}</span>
                )}
              </div>

              {/* 글 내용 */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6 whitespace-pre-wrap text-gray-700">
                {selectedPost.content}
              </div>

              {/* 삭제 버튼 */}
              <div className="flex justify-end mb-4">
                <button
                  onClick={() => handleDeletePost(selectedPost.id)}
                  disabled={deleteLoading}
                  className="px-3 py-1.5 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors"
                >
                  {deleteLoading ? '삭제 중...' : '글 삭제'}
                </button>
              </div>

              {/* 댓글 섹션 (개별 메시지만) */}
              {selectedPost.post_type === 'individual' && (
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  댓글 ({selectedPost.comments?.length || 0})
                </h3>

                {/* 대화 스레드 (말풍선) */}
                <div className="bg-gray-50 rounded-lg p-3 mb-4 max-h-[45vh] overflow-y-auto space-y-3">
                  {selectedPost.comments && selectedPost.comments.length > 0 ? (
                    selectedPost.comments.map((comment) => {
                      const mine = comment.author_type === 'owner' || comment.author_type === 'staff';
                      return (
                        <div
                          key={comment.id}
                          className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[75%] ${mine ? 'items-end' : 'items-start'} flex flex-col`}>
                            {!mine && (
                              <span className="text-xs text-gray-500 mb-0.5 ml-1">{comment.author_name}</span>
                            )}
                            <div className="flex items-end gap-1.5">
                              {mine && (
                                <span className="text-[10px] text-gray-400 whitespace-nowrap">
                                  {formatDateTime(comment.created_at)}
                                </span>
                              )}
                              <div
                                className={`px-3 py-2 rounded-2xl whitespace-pre-wrap break-words ${
                                  mine
                                    ? 'bg-blue-600 text-white rounded-br-sm'
                                    : 'bg-white text-gray-800 border border-gray-200 rounded-bl-sm'
                                }`}
                              >
                                {comment.content}
                              </div>
                              {!mine && (
                                <span className="text-[10px] text-gray-400 whitespace-nowrap">
                                  {formatDateTime(comment.created_at)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-gray-400 text-sm text-center py-6">아직 대화가 없습니다.</p>
                  )}
                </div>

                {/* 채팅 입력바 */}
                <div className="flex gap-2 items-end">
                  <textarea
                    value={commentContent}
                    onChange={(e) => setCommentContent(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        if (!commentLoading && commentContent.trim()) handleSubmitComment();
                      }
                    }}
                    placeholder="메시지를 입력하세요 (Enter 전송, Shift+Enter 줄바꿈)"
                    rows={1}
                    className="flex-1 border border-gray-300 rounded-2xl px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none max-h-32"
                  />
                  <button
                    onClick={handleSubmitComment}
                    disabled={commentLoading || !commentContent.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 transition-colors shrink-0"
                  >
                    {commentLoading ? '...' : '전송'}
                  </button>
                </div>
              </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 상세 로딩 오버레이 */}
      {detailLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg px-6 py-4 shadow-lg">
            <p className="text-gray-700">로딩 중...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentCommunicationPage;
